/**
 * Match-rate helper for the Job Runs composition-bars chart (bd-deepcontext-hfv7u.4).
 *
 * A run's match rate is `matchedUrls / totalUrls`. The chart draws this as the
 * green portion of each bar (the red remainder is `1 - matchRate`), so the value
 * is always clamped to [0, 1] and guarded against a zero (or missing) total —
 * an empty run must not divide by zero and must render as a 0% (all-red) bar.
 */

import { ScheduledJobRun } from "../components/jobRuns/types";

/**
 * Fraction of processed URLs that matched, in [0, 1].
 *
 * Returns 0 when `total` is 0, negative, or non-finite (guards divide-by-zero),
 * and clamps to [0, 1] so a bad `matched > total` payload can't overflow the bar.
 *
 * Examples:
 *   - matchRate(50, 100) → 0.5
 *   - matchRate(0, 0)    → 0   (no divide-by-zero)
 *   - matchRate(5, 0)    → 0
 *   - matchRate(120, 100)→ 1   (clamped)
 */
export function matchRate(matched: number, total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  if (!Number.isFinite(matched) || matched <= 0) return 0;
  const rate = matched / total;
  if (rate <= 0) return 0;
  if (rate >= 1) return 1;
  return rate;
}

/**
 * Match rate for a run, reading `resultsSummary.{matchedUrls,totalUrls}`.
 *
 * Runs without a results summary (pending / freshly-started) have no known
 * total yet, so they report a 0 rate — the chart renders them as a dimmed,
 * all-red bar with a status tick rather than an empty gap.
 */
export function runMatchRate(run: ScheduledJobRun): number {
  const summary = run.resultsSummary;
  if (!summary) return 0;
  return matchRate(summary.matchedUrls, summary.totalUrls);
}

/**
 * Fraction of submitted URLs that were dropped/not-classified, in [0, 1]
 * (bd-deepcontext-qx0pm). `dropped / total`, guarded against a zero/negative/
 * non-finite total (returns 0 — no divide-by-zero) and clamped to [0, 1].
 *
 * `dropped` = URLs submitted to the pipeline that never returned a
 * classification (chunk timeouts / extraction failures), recorded by bd-wte47.
 * matched + unmatched + dropped = total, so this band is the top slice of the
 * 0–100% composition bar carved out of what would otherwise read as unmatched.
 */
export function droppedRate(dropped: number, total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  if (!Number.isFinite(dropped) || dropped <= 0) return 0;
  const rate = dropped / total;
  if (rate <= 0) return 0;
  if (rate >= 1) return 1;
  return rate;
}

/**
 * Dropped rate for a run, reading `resultsSummary.{droppedUrls,totalUrls}`.
 *
 * Returns 0 when the summary or `droppedUrls` is missing (runs predating
 * bd-wte47, or pending runs with no known total) — the chart then renders no
 * amber band, identical to the pre-drop UI.
 */
export function runDroppedRate(run: ScheduledJobRun): number {
  const summary = run.resultsSummary;
  if (!summary) return 0;
  return droppedRate(summary.droppedUrls ?? 0, summary.totalUrls);
}

/**
 * Fulfillment rate for the Job Runs chart (bd-deepcontext-hfv7u.13), in [0, 1].
 *
 * `fulfilled / requested`, where `requested` is the job's per-run target
 * (`job.urlsPerRun`) and `fulfilled` is `run.urlsProcessed`. Guards a zero,
 * negative, or non-finite target (returns 0 — no divide-by-zero) and clamps to
 * [0, 1] so an over-fulfilled run can't overflow the % axis.
 *
 * Caveat: only the CURRENT `job.urlsPerRun` is stored, so the chart uses it as
 * the denominator for every run in the window (no per-run historical target).
 *
 * Examples:
 *   - fulfillmentRate(800, 1000) → 0.8
 *   - fulfillmentRate(100, 0)    → 0   (no divide-by-zero)
 *   - fulfillmentRate(1200, 1000)→ 1   (clamped)
 */
export function fulfillmentRate(fulfilled: number, requested: number): number {
  if (!Number.isFinite(requested) || requested <= 0) return 0;
  if (!Number.isFinite(fulfilled) || fulfilled <= 0) return 0;
  const rate = fulfilled / requested;
  if (rate <= 0) return 0;
  if (rate >= 1) return 1;
  return rate;
}

/**
 * Fulfillment rate for a run against the job's current per-run target.
 *
 * Returns 0 when the target is missing/0 (the chart then omits the fulfillment
 * line entirely rather than drawing a flat-zero series).
 */
export function runFulfillmentRate(
  run: ScheduledJobRun,
  urlsPerRun: number | undefined,
): number {
  if (urlsPerRun === undefined) return 0;
  return fulfillmentRate(run.urlsProcessed, urlsPerRun);
}
